import React, { useEffect, useState } from "react";
import { useOrganisations } from "./../Auth/useOrganisations";
import { LinearProgress, linearProgressClasses, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Link } from "react-router-dom";
import { getOrderAnalytics, getProductPerformance } from "../Services/CartApi";
import {
  getCreditSummary,
  getClientCreditEvolution,
  getTopOrderClients,
} from "../Services/ClientApi";
import GifLoader from "../Components/loader";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Legend,
} from "recharts";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: "#172b4c",
  },
}));

function Main() {
  const { t } = useTranslation();
  const [
    organisations,
    setOrganisations,
    usedOrganisations,
    setUsedOrganisations,
  ] = useOrganisations();

  const SortButton = ({ field, label, activeField, activeDir, onSort }) => {
    const isActive = activeField === field;
    return (
      <button
        className={`btn btn-xs ${isActive ? "btn-primary" : "btn-outline-secondary"} mr-1`}
        style={{ fontSize: "11px", padding: "2px 8px" }}
        onClick={() => onSort(field)}
        title={label}
      >
        {label}
        {isActive && (
          <i
            className={`fa fa-sort-${activeDir === "desc" ? "down" : "up"} ml-1`}
          />
        )}
      </button>
    );
  };
  // 1. Date States (Default to current month)
  const [dateDebut, setDateDebut] = useState(
    moment().startOf("month").format("YYYY-MM-DD"),
  );
  const [dateFin, setDateFin] = useState(
    moment().endOf("month").format("YYYY-MM-DD"),
  );
  const [productPerformance, setProductPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({
    summary: { totalCarts: 0, totalRevenue: 0, totalSessions: 0 },
    topProducts: [],
    hourlySessions: [],
    cashierStats: [],
    categorySales: [],
  });
  const [cashierSortField, setCashierSortField] = useState("month.carts"); // default: most orders this month
  const [cashierSortDir, setCashierSortDir] = useState("desc");
  const sortedCashiers = [...(analytics.cashierStats ?? [])].sort((a, b) => {
    const getValue = (item, field) => {
      if (field === "month.carts") return item.month?.carts ?? 0;
      if (field === "month.revenue") return item.month?.revenue ?? 0;
      if (field === "today.carts") return item.today?.carts ?? 0;
      if (field === "today.revenue") return item.today?.revenue ?? 0;
      return 0;
    };
    const diff = getValue(a, cashierSortField) - getValue(b, cashierSortField);
    return cashierSortDir === "desc" ? -diff : diff;
  });
  // — add these near your other useState declarations —
  const [perfExpanded, setPerfExpanded] = useState(false);
  const [perfPage, setPerfPage] = useState(1);
  const [perfHasMore, setPerfHasMore] = useState(false);
  const [perfTotal, setPerfTotal] = useState(0);
  const [perfLoadingMore, setPerfLoadingMore] = useState(false);
  const [creditStats, setCreditStats] = useState({
    todayDebit: 0,
    totalOutstanding: 0,
  });
  const [topDebtors, setTopDebtors] = useState([]);
  const [creditEvolution, setCreditEvolution] = useState([]);
  const [topOrderClients, setTopOrderClients] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!usedOrganisations) return;
      try {
        setLoading(true);
        const params = {
          organisation_id: usedOrganisations,
          date_debut: dateDebut,
          date_fin: dateFin,
        };

        // Fetch all APIs in parallel
        const [
          resAnalytics,
          resPerformance,
          resCredit,
          resEvolution,
          resTopOrders,
        ] = await Promise.all([
          getOrderAnalytics(params),
          getProductPerformance(params),
          getCreditSummary(usedOrganisations),
          getClientCreditEvolution(usedOrganisations, 30),
          getTopOrderClients(usedOrganisations, 5),
        ]);

        if (resAnalytics.data.success) setAnalytics(resAnalytics.data.data);
        if (resPerformance.data.success) {
          const { data, pagination } = resPerformance.data.data;
          setProductPerformance(data);
          setPerfHasMore(pagination.hasMore);
          setPerfTotal(pagination.total);
          setPerfExpanded(false);
          setPerfPage(1);
        }
        if (resCredit?.data?.success) {
          const {
            todayDebit,
            totalOutstanding,
            clients: debtors,
          } = resCredit.data.data;
          setCreditStats({
            todayDebit: todayDebit ?? 0,
            totalOutstanding: totalOutstanding ?? 0,
          });
          setTopDebtors((debtors || []).slice(0, 10));
        }
        if (resEvolution?.data?.success) {
          setCreditEvolution(resEvolution.data.data || []);
        }
        if (resTopOrders?.data?.success) {
          setTopOrderClients(resTopOrders.data.data || []);
        }
      } catch (err) {
        console.error("Dashboard Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [usedOrganisations, dateDebut, dateFin]);
  const loadMorePerf = async () => {
    setPerfLoadingMore(true);
    try {
      const nextPage = perfPage + 1;
      const res = await getProductPerformance({
        organisation_id: usedOrganisations,
        date_debut: dateDebut,
        date_fin: dateFin,
        limit: 20, // bigger pages once expanded
        page: nextPage,
      });
      if (res.data.success) {
        const { data, pagination } = res.data.data;
        setProductPerformance((prev) => [...prev, ...data]);
        setPerfPage(nextPage);
        setPerfHasMore(pagination.hasMore);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setPerfLoadingMore(false);
    }
  };

  // Called when the user clicks "Voir plus" the very first time
  const expandPerf = async () => {
    setPerfExpanded(true);
    setPerfLoadingMore(true);
    try {
      // Fetch page 1 of the full paginated view (20 per page)
      const res = await getProductPerformance({
        organisation_id: usedOrganisations,
        date_debut: dateDebut,
        date_fin: dateFin,
        limit: 20,
        page: 1,
      });
      if (res.data.success) {
        const { data, pagination } = res.data.data;
        setProductPerformance(data);
        setPerfPage(1);
        setPerfHasMore(pagination.hasMore);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setPerfLoadingMore(false);
    }
  };
  // Labels for the category donuts
  const nowDate = new Date();
  const todayLabel = `${String(nowDate.getDate()).padStart(2, "0")}-${String(
    nowDate.getMonth() + 1,
  ).padStart(2, "0")}-${nowDate.getFullYear()}`;
  const monthLabel = `${String(nowDate.getMonth() + 1).padStart(2, "0")}-${nowDate.getFullYear()}`;
  const monthName = nowDate.toLocaleDateString("fr-FR", { month: "long" });
  const yearLabel = nowDate.getFullYear();

  // Label for the selected period (from dateDebut) -> "06-2026 juin"
  const selectedMonthLabel = (() => {
    const [y, m] = dateDebut.split("-").map(Number);
    const d = new Date(y, m - 1, 1);
    const mm = String(m).padStart(2, "0");
    const name = d.toLocaleDateString("fr-FR", { month: "long" });
    return `${mm}-${y} ${name}`;
  })();

  return (
    <div className="container-full">
      {/* <div className="content-header">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h3 className="page-title">{t("Tableau de bord")}</h3>
          </div>

          <div className="d-flex align-items-center" style={{ gap: "15px" }}>
            <div className="form-group mb-0">
              <label className="mb-0">{t("Date début")}</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
              />
            </div>
            <div className="form-group mb-0">
              <label className="mb-0">{t("Date fin")}</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div> */}

      <section className="content">
        {loading ? (
          <GifLoader />
        ) : (
          <>
            {/* --- KPI Stats Row --- */}
            <div className="row">
              <DetailedStatCard
                label="Aujourd'hui"
                count={analytics?.summary?.totalSessions?.today ?? 0}
                revenue={analytics?.summary?.totalSessions?.todayCA}
                icon="fa-shopping-bag"
                color="primary"
              />
              <DetailedStatCard
                label="Ce mois-ci"
                count={analytics?.summary?.totalSessions?.month ?? 0}
                revenue={analytics?.summary?.totalSessions?.monthCA}
                icon="fa-shopping-bag"
                color="info"
              />
              <DetailedStatCard
                label="Cette année"
                count={analytics?.summary?.totalSessions?.year ?? 0}
                revenue={analytics?.summary?.totalSessions?.yearCA}
                icon="fa-shopping-bag"
                color="success"
              />
              {/* <StatCard
                title={t("Total Commandes")}
                value={analytics.summary.totalCarts}
                icon="fa-shopping-basket"
                color="info"
              /> */}
              {/* <StatCard
                title={t("Chiffre d'affaires")}
                value={`${analytics?.summary?.totalRevenue?.toFixed(3)} DT`}
                icon="fa-money"
                color="success"
              /> */}
              <CreditStatCard
                todayDebit={creditStats.todayDebit}
                totalOutstanding={creditStats.totalOutstanding}
              />
              <StatCard
                title={t("Recette")}
                value={`${analytics?.summary?.recette?.total.toFixed(3) ?? 0} DT `}
                icon="fa-money-bill"
                color="success"
              />
              <StatCard
                title={t("Espèces")}
                value={`${analytics?.summary?.recette?.cash.toFixed(3) ?? 0} DT `}
                icon="fa-money-bill"
                color="success"
              />
              <StatCard
                title={t("Chèque")}
                value={`${analytics?.summary?.recette?.cheque.toFixed(3) ?? 0} DT `}
                icon="fa-money-check"
                color="info"
              />
              <StatCard
                title={t("Carte bancaire")}
                value={`${analytics?.summary?.recette.creditcard.toFixed(3) ?? 0} DT `}
                icon="fa-credit-card"
                color="primary"
              />
            </div>

            {/* --- Hourly Sessions Chart --- */}
            <div className="row">
              <div className="col-12">
                <div className="box">
                  <div className="box-header with-border">
                    <h4 className="box-title">
                      {t("Commandes par heure")} ({selectedMonthLabel})
                    </h4>
                  </div>
                  <div className="box-body">
                    {analytics?.hourlySessions?.length > 0 ? (
                      <div style={{ width: "100%", height: 320 }}>
                        <ResponsiveContainer>
                          <BarChart
                            data={analytics?.hourlySessions}
                            margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              vertical={false}
                            />
                            <XAxis
                              dataKey="label"
                              interval={1}
                              tick={{ fontSize: 12 }}
                            />
                            <YAxis
                              allowDecimals={false}
                              tick={{ fontSize: 12 }}
                            />
                            <Tooltip
                              formatter={(val) => [val, t("Commandes")]}
                              labelFormatter={(label) =>
                                `${t("Heure")} ${label}`
                              }
                            />
                            <Bar
                              dataKey="count"
                              name={t("Commandes")}
                              fill="#172b4c"
                              radius={[4, 4, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-center mb-0 text-mute">
                        {t("Aucune donnée pour cette période")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              {/* --- Top 5 Products Table --- */}
              <div className="col-xl-6 col-12">
                <div className="box">
                  <div className="box-header with-border">
                    <h4 className="box-title">{t("Best seller")}</h4>
                  </div>
                  <div className="box-body">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr>
                            <th>{t("Produit")}</th>
                            <th>{t("Quantité")}</th>
                            <th>{t("Total Ventes")}</th>
                            {/* <th style={{ width: "30%" }}>{t("Popularité")}</th> */}
                          </tr>
                        </thead>
                        <tbody>
                          {analytics?.topProducts?.length > 0 ? (
                            analytics?.topProducts?.map((item, index) => (
                              <tr key={index}>
                                <td className="font-weight-600 text-fade">
                                  {item.name.fr}
                                </td>
                                <td>
                                  <span className="badge badge-pill badge-primary-light">
                                    {item.quantity}
                                  </span>
                                </td>
                                <td>{item.totalSales?.toFixed(3)} DT</td>
                                {/* <td>
                                  <BorderLinearProgress
                                    variant="determinate"
                                    value={
                                      (item.quantity /
                                        (analytics?.topProducts[0]?.quantity ||
                                          1)) *
                                      100
                                    }
                                  />
                                </td> */}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center">
                                {t("Aucune donnée pour cette période")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* --- Worst 5 Products Table --- */}
              <div className="col-xl-6 col-12">
                <div className="box">
                  <div className="box-header with-border">
                    <h4 className="box-title">{t("Worst seller")}</h4>
                  </div>
                  <div className="box-body">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr>
                            <th>{t("Produit")}</th>
                            <th>{t("Quantité")}</th>
                            <th>{t("Total Ventes")}</th>
                            {/* <th style={{ width: "30%" }}>{t("Popularité")}</th> */}
                          </tr>
                        </thead>
                        <tbody>
                          {analytics?.worstProducts?.length > 0 ? (
                            analytics?.worstProducts?.map((item, index) => {
                              const maxQty = Math.max(
                                ...analytics?.worstProducts?.map(
                                  (p) => p.quantity,
                                ),
                                1,
                              );
                              return (
                                <tr key={index}>
                                  <td className="font-weight-600 text-fade">
                                    {item.name.fr}
                                  </td>
                                  <td>
                                    <span className="badge badge-pill badge-warning-light">
                                      {item.quantity}
                                    </span>
                                  </td>
                                  <td>{item.totalSales?.toFixed(3)} DT</td>
                                  {/* <td>
                                    <BorderLinearProgress
                                      variant="determinate"
                                      value={(item.quantity / maxQty) * 100}
                                    />
                                  </td> */}
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center">
                                {t("Aucune donnée pour cette période")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* --- Sales per category (gamme) donuts --- */}
            <div className="row">
              <CategoryDonut
                title={`${t("Ventes par gamme Aujourd'hui")} (${todayLabel})`}
                data={analytics.categorySales}
                dataKey="todaySales"
              />
              <CategoryDonut
                title={`${t("Ventes par gamme")} ${monthName} (${monthLabel})`}
                data={analytics.categorySales}
                dataKey="monthSales"
              />
              <CategoryDonut
                title={`${t("Ventes par gamme année")} ${yearLabel}`}
                data={analytics.categorySales}
                dataKey="yearSales"
              />
            </div>

            {/* --- Carts per Cashier --- */}
            <div className="row">
              <div className="col-12">
                <div className="box">
                  <div
                    className="box-header with-border d-flex align-items-center justify-content-between flex-wrap"
                    style={{ gap: "8px" }}
                  >
                    <h4 className="box-title mb-0">
                      {t("Commandes par caissier")}
                    </h4>

                    {/* Sort controls */}
                    <div
                      className="d-flex align-items-center flex-wrap"
                      style={{ gap: "4px" }}
                    >
                      <span
                        className="text-muted mr-2"
                        style={{ fontSize: "12px" }}
                      >
                        {t("Trier par")} :
                      </span>
                      <SortButton
                        field="month.carts"
                        label={t("Cmds / mois")}
                        activeField={cashierSortField}
                        activeDir={cashierSortDir}
                        onSort={(f) => {
                          if (cashierSortField === f)
                            setCashierSortDir((d) =>
                              d === "desc" ? "asc" : "desc",
                            );
                          else {
                            setCashierSortField(f);
                            setCashierSortDir("desc");
                          }
                        }}
                      />
                      <SortButton
                        field="month.revenue"
                        label={t("CA / mois")}
                        activeField={cashierSortField}
                        activeDir={cashierSortDir}
                        onSort={(f) => {
                          if (cashierSortField === f)
                            setCashierSortDir((d) =>
                              d === "desc" ? "asc" : "desc",
                            );
                          else {
                            setCashierSortField(f);
                            setCashierSortDir("desc");
                          }
                        }}
                      />
                      <SortButton
                        field="today.carts"
                        label={t("Cmds / aujourd'hui")}
                        activeField={cashierSortField}
                        activeDir={cashierSortDir}
                        onSort={(f) => {
                          if (cashierSortField === f)
                            setCashierSortDir((d) =>
                              d === "desc" ? "asc" : "desc",
                            );
                          else {
                            setCashierSortField(f);
                            setCashierSortDir("desc");
                          }
                        }}
                      />
                      <SortButton
                        field="today.revenue"
                        label={t("CA / aujourd'hui")}
                        activeField={cashierSortField}
                        activeDir={cashierSortDir}
                        onSort={(f) => {
                          if (cashierSortField === f)
                            setCashierSortDir((d) =>
                              d === "desc" ? "asc" : "desc",
                            );
                          else {
                            setCashierSortField(f);
                            setCashierSortDir("desc");
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="box-body">
                    <div className="table-responsive">
                      <table className="table table-hover table-bordered mb-0">
                        <thead>
                          <tr>
                            <th rowSpan="2" className="align-middle">
                              {t("Caissier")}
                            </th>
                            <th colSpan="3" className="text-center">
                              {t("Aujourd'hui")} (
                              {(() => {
                                const d = new Date();
                                return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
                              })()}
                              )
                            </th>
                            <th colSpan="2" className="text-center">
                              {t("Ce mois")} (
                              {(() => {
                                const d = new Date();
                                return `${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
                              })()}
                              )
                            </th>
                          </tr>
                          <tr>
                            <th>{t("Début")}</th>
                            <th className="text-right">{t("Nb Commandes")}</th>
                            <th className="text-right">
                              {t("Chiffre d'affaire")}
                            </th>
                            <th className="text-right">{t("Nb Commandes")}</th>
                            <th className="text-right">
                              {t("Chiffre d'affaire")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedCashiers.length > 0 ? (
                            sortedCashiers.map((item, index) => (
                              <tr key={index}>
                                <td className="font-weight-600">
                                  {item.name || item._id}
                                </td>
                                <td>
                                  {item.today?.firstOrder
                                    ? new Date(
                                        item.today.firstOrder,
                                      ).toLocaleTimeString("fr-FR", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })
                                    : "—"}
                                </td>
                                <td className="text-right">
                                  <span className="badge badge-pill badge-primary-light">
                                    {item.today?.carts || 0}
                                  </span>
                                </td>
                                <td className="text-right">
                                  {(item.today?.revenue || 0).toFixed(3)} DT
                                </td>
                                <td className="text-right">
                                  <span className="badge badge-pill badge-primary-light">
                                    {item.month?.carts || 0}
                                  </span>
                                </td>
                                <td className="text-right">
                                  {(item.month?.revenue || 0).toFixed(3)} DT
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center">
                                {t("Aucune donnée pour cette période")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* --- Rentabilité par Produit --- */}
            <div className="row">
              <div className="col-12">
                <div className="box">
                  <div className="box-header with-border d-flex align-items-center justify-content-between">
                    <h4 className="box-title mb-0">
                      {t("Rentabilité par Produit")} ({selectedMonthLabel})
                      {perfExpanded && (
                        <span
                          className="text-muted ml-2"
                          style={{ fontSize: "13px", fontWeight: 400 }}
                        >
                          — {productPerformance.length} / {perfTotal}{" "}
                          {t("produits")}
                        </span>
                      )}
                    </h4>

                    {!perfExpanded && perfTotal > 5 && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={expandPerf}
                        disabled={perfLoadingMore}
                      >
                        <i className="fa fa-expand mr-1" />
                        {t("Voir plus")} ({perfTotal})
                      </button>
                    )}

                    {perfExpanded && (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                          // Collapse back: refetch the 5-item preview
                          setPerfExpanded(false);
                          setPerfPage(1);
                          getProductPerformance({
                            organisation_id: usedOrganisations,
                            date_debut: dateDebut,
                            date_fin: dateFin,
                            limit: 5,
                            page: 1,
                          }).then((res) => {
                            if (res.data.success) {
                              const { data, pagination } = res.data.data;
                              setProductPerformance(data);
                              setPerfHasMore(pagination.hasMore);
                            }
                          });
                        }}
                      >
                        <i className="fa fa-compress mr-1" />
                        {t("Réduire")}
                      </button>
                    )}
                  </div>

                  <div className="box-body">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>{t("Produit")}</th>
                            <th className="text-right">{t("Qté Achetée")}</th>
                            <th className="text-right">{t("Coût d'Achat")}</th>
                            <th className="text-right">{t("Qté Vendue")}</th>
                            <th className="text-right">{t("Ventes (CA)")}</th>
                            <th className="text-right">{t("Marge Brute")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productPerformance.length > 0 ? (
                            productPerformance.map((item, index) => {
                              const margin =
                                item.salesTotal - item.purchaseTotal;
                              return (
                                <tr key={index}>
                                  <td className="font-weight-600">
                                    {item.name?.fr ?? item.name}
                                  </td>
                                  <td className="text-right">
                                    {item.purchaseQty}
                                  </td>
                                  <td className="text-right text-primary">
                                    {item.purchaseTotal?.toFixed(3)} DT
                                  </td>
                                  <td className="text-right">
                                    {item.salesQty}
                                  </td>
                                  <td className="text-right text-primary">
                                    {item.salesTotal?.toFixed(3)} DT
                                  </td>
                                  <td
                                    className={`text-right font-weight-bold ${margin >= 0 ? "text-success" : "text-primary"}`}
                                  >
                                    {margin.toFixed(3)} DT
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center">
                                {t("Aucune donnée pour cette période")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Load more — only shown in expanded mode */}
                    {perfExpanded && perfHasMore && (
                      <div className="text-center mt-3">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={loadMorePerf}
                          disabled={perfLoadingMore}
                        >
                          {perfLoadingMore ? (
                            <>
                              <i className="fa fa-spinner fa-spin mr-1" />
                              {t("Chargement...")}
                            </>
                          ) : (
                            <>
                              <i className="fa fa-chevron-down mr-1" />
                              {t("Charger plus")}
                            </>
                          )}
                        </button>
                      </div>
                    )}

                    {perfLoadingMore && !perfHasMore && (
                      <p
                        className="text-center text-muted mt-2"
                        style={{ fontSize: "12px" }}
                      >
                        {t("Tous les produits sont affichés")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* --- Top 5 clients par commandes --- */}
            <div className="row">
              {/* <div className="col-xl-6 col-12">
                <div className="box">
                  <div className="box-header with-border">
                    <h4 className="box-title">
                      <i
                        className="fa fa-user-clock mr-2"
                        style={{ color: "#fd683e" }}
                      />
                      {t("Top débiteurs")}
                    </h4>
                  </div>
                  <div className="box-body">
                    {topDebtors.length > 0 ? (
                      <div style={{ width: "100%", height: 300 }}>
                        <ResponsiveContainer>
                          <BarChart
                            data={topDebtors.map((c) => ({
                              name:
                                (c.name || "").length > 18
                                  ? (c.name || "").slice(0, 16) + "…"
                                  : c.name || "",
                              credit: parseFloat((c.credit || 0).toFixed(3)),
                            }))}
                            layout="vertical"
                            margin={{ top: 0, right: 40, left: 10, bottom: 0 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              horizontal={false}
                            />
                            <XAxis
                              type="number"
                              tick={{ fontSize: 11 }}
                              tickFormatter={(v) => `${v} DT`}
                            />
                            <YAxis
                              type="category"
                              dataKey="name"
                              width={120}
                              tick={{ fontSize: 11 }}
                            />
                            <Tooltip
                              formatter={(v) => [
                                `${Number(v).toFixed(3)} DT`,
                                t("Crédit encours"),
                              ]}
                            />
                            <Bar
                              dataKey="credit"
                              fill="#fd683e"
                              radius={[0, 4, 4, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-center text-muted py-4">
                        {t("Aucun crédit impayé")}
                      </p>
                    )}
                  </div>
                </div>
              </div> */}
              <div className="col-xl-12 col-12">
                <div className="box">
                  <div className="box-header with-border">
                    <h4 className="box-title">
                      <i
                        className="fa fa-trophy mr-2"
                        style={{ color: "#6d28d9" }}
                      />
                      {t("Top 5 clients")}
                    </h4>
                  </div>
                  <div className="box-body">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th style={{ width: "50px" }}></th>
                            <th>{t("Client")}</th>
                            <th
                              className="text-center"
                              style={{ width: "160px" }}
                            >
                              {t("Total commandes")}
                            </th>
                            <th
                              className="text-right"
                              style={{ width: "180px" }}
                            >
                              {t("Montant total")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {topOrderClients.length > 0 ? (
                            topOrderClients.map((c, idx) => (
                              <tr key={c._id || idx}>
                                <td>
                                  <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                    style={{
                                      width: 28,
                                      height: 28,
                                      fontSize: 13,
                                      fontWeight: 700,
                                      background:
                                        idx === 0
                                          ? "#fd683e"
                                          : idx === 1
                                            ? "#6d28d9"
                                            : "rgba(0,0,0,0.07)",
                                      color: idx < 2 ? "#fff" : "#555",
                                    }}
                                  >
                                    {idx + 1}
                                  </span>
                                </td>
                                <td className="font-weight-600">
                                  {c.name}
                                  {c.code && (
                                    <span
                                      className="text-muted ml-2"
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: 400,
                                      }}
                                    >
                                      {c.code}
                                    </span>
                                  )}
                                </td>
                                <td className="text-center">
                                  <span
                                    className="badge badge-pill"
                                    style={{
                                      background: "rgba(253,104,62,0.1)",
                                      color: "#fd683e",
                                      fontSize: "13px",
                                      padding: "4px 12px",
                                    }}
                                  >
                                    {c.totalOrders}
                                  </span>
                                </td>
                                <td
                                  className="text-right font-weight-600"
                                  style={{ color: "#172b4c" }}
                                >
                                  {(c.totalAmount || 0).toFixed(3)} DT
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan="4"
                                className="text-center py-4 text-muted"
                              >
                                {t("Aucune commande client enregistrée")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* --- Rapports Clients --- */}
            <div className="row">
              {/* Report 1: Top débiteurs */}

              {/* Report 2: Évolution du crédit (30 jours) */}
              {/* <div className="col-xl-6 col-12">
                <div className="box">
                  <div className="box-header with-border">
                    <h4 className="box-title">
                      <i className="fa fa-chart-area mr-2" style={{ color: "#6d28d9" }} />
                      {t("Évolution du crédit")} (30j)
                    </h4>
                  </div>
                  <div className="box-body">
                    {creditEvolution.length > 0 ? (
                      <div style={{ width: "100%", height: 300 }}>
                        <ResponsiveContainer>
                          <AreaChart
                            data={creditEvolution.map((d) => ({
                              date: d._id ? d._id.slice(5).split("-").reverse().join("/") : "",
                              Débits: parseFloat((d.debits || 0).toFixed(3)),
                              Paiements: parseFloat((d.payments || 0).toFixed(3)),
                            }))}
                            margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                          >
                            <defs>
                              <linearGradient id="gradDebits" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#fd683e" stopOpacity={0.2} />
                                <stop offset="95%" stopColor="#fd683e" stopOpacity={0} />
                              </linearGradient>
                              <linearGradient id="gradPaiements" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#6d28d9" stopOpacity={0.2} />
                                <stop offset="95%" stopColor="#6d28d9" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}`} width={60} />
                            <Tooltip formatter={(v, name) => [`${Number(v).toFixed(3)} DT`, name]} />
                            <Legend />
                            <Area type="monotone" dataKey="Débits" stroke="#fd683e" fill="url(#gradDebits)" strokeWidth={2} dot={false} />
                            <Area type="monotone" dataKey="Paiements" stroke="#6d28d9" fill="url(#gradPaiements)" strokeWidth={2} dot={false} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-center text-muted py-4">{t("Aucune activité de crédit sur les 30 derniers jours")}</p>
                    )}
                  </div>
                </div>
              </div> */}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

const DONUT_COLORS = [
  "#172b4c",
  "#e53935",
  "#43a047",
  "#8e24aa",
  "#fb8c00",
  "#1e88e5",
  "#00acc1",
  "#f06292",
  "#c0ca33",
  "#6d4c41",
  "#5e35b1",
  "#26a69a",
];

const CategoryDonut = ({ title, data, dataKey }) => {
  const { t } = useTranslation();

  const rows = (data || [])
    .map((c) => ({
      name: c.name || c._id,
      value: Number((c[dataKey] || 0).toFixed(3)),
    }))
    .filter((c) => c.value > 0)
    .sort((a, b) => b.value - a.value);

  const total = rows.reduce((s, r) => s + r.value, 0);

  const fmt = (v) =>
    v.toLocaleString("fr-FR", {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });

  return (
    <div className="col-xl-4 col-12 d-flex">
      <div className="box flex-fill">
        <div className="box-header with-border">
          <h4 className="box-title">{title}</h4>
        </div>
        <div className="box-body">
          {rows.length > 0 ? (
            <>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={rows}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={100}
                      paddingAngle={1}
                    >
                      {rows.map((_, i) => (
                        <Cell
                          key={i}
                          fill={DONUT_COLORS[i % DONUT_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(val) => {
                        const pct = total
                          ? ((val / total) * 100).toFixed(2)
                          : "0";
                        return [`${fmt(val)} DT (${pct}%)`, ""];
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Legend: category name + value */}
              <ul className="list-unstyled mb-0 mt-3">
                {rows.map((r, i) => (
                  <li
                    key={i}
                    className="d-flex align-items-center justify-content-between mb-1"
                    style={{ fontSize: "13px" }}
                  >
                    <span className="d-flex align-items-center">
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 2,
                          background: DONUT_COLORS[i % DONUT_COLORS.length],
                          display: "inline-block",
                          marginRight: 8,
                          flexShrink: 0,
                        }}
                      />
                      {r.name}
                    </span>
                    <span className="font-weight-600">{fmt(r.value)}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-center mb-0 text-mute">{t("Aucune donnée")}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const CreditStatCard = ({ todayDebit, totalOutstanding }) => (
  <div className="col-xl-3 col-md-6 col-12 mb-4">
    <div
      className="box card border-0 shadow-sm"
      style={{ borderRadius: "12px", overflow: "hidden" }}
    >
      <div className="box-body p-4">
        {/* Top section */}
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <span
              className="badge mb-2 px-2 py-1"
              style={{
                fontSize: "11px",
                fontWeight: "600",
                letterSpacing: "0.5px",
                background: "rgba(253,104,62,0.12)",
                color: "#fd683e",
              }}
            >
              Crédit clients
            </span>
            <h2
              className="mb-0 font-weight-700 text-dark"
              style={{
                fontSize: "1.8rem",
                lineHeight: "1.2",
                color: "#fd683e",
              }}
            >
              {todayDebit.toFixed(3)}
              <span
                className="text-muted ml-2"
                style={{ fontSize: "14px", fontWeight: "400" }}
              >
                DT
              </span>
            </h2>
          </div>
          <div
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: "45px",
              height: "45px",
              background: "rgba(253,104,62,0.12)",
            }}
          >
            <i
              className="fa fa-user-clock"
              style={{ fontSize: "20px", color: "#fd683e" }}
            ></i>
          </div>
        </div>

        <hr
          className="my-3"
          style={{ borderTop: "1px solid #f1f1f4", margin: "15px 0" }}
        />

        {/* Bottom section */}
        <div className="d-flex justify-content-between align-items-center">
          <span className="text-muted" style={{ fontSize: "13px" }}>
            Total encours
          </span>
          <Link
            to="/clients/credits"
            style={{
              fontSize: "15px",
              fontWeight: "700",
              color: "#6d28d9",
              textDecoration: "none",
            }}
          >
            {totalOutstanding.toFixed(3)} DT
          </Link>
        </div>
      </div>
    </div>
  </div>
);

const StatCard = ({ title, value, icon, color }) => (
  <div className="col-xl-3 col-md-6 col-12">
    <div className="box">
      <div className="box-body">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h3 className="mb-0 font-weight-700">{value}</h3>
            <p className="text-mute mb-0">{title}</p>
          </div>
          <div className={`bg-${color}-light rounded p-10`}>
            <i className={`fa ${icon} text-${color} fontSize-24`}></i>
          </div>
        </div>
      </div>
    </div>
  </div>
);
const DetailedStatCard = ({ label, count, revenue, icon, color }) => {
  // Gracefully handle undefined/null revenue values
  const formattedRevenue =
    typeof revenue === "number" ? revenue.toFixed(3) : "0.000";

  return (
    <div className="col-xl-3 col-md-6 col-12 mb-4">
      <div
        className="box card border-0 shadow-sm"
        style={{ borderRadius: "12px", overflow: "hidden" }}
      >
        <div className="box-body p-4">
          {/* Top section: Metrics & Icon */}
          <div className="d-flex justify-content-between align-items-start">
            <div>
              {/* Period Badge */}
              <span
                className={`badge bg-${color}-light text-${color} mb-2 px-2 py-1`}
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  letterSpacing: "0.5px",
                }}
              >
                {label}
              </span>

              {/* Large Session Count */}
              <h2
                className="mb-0 font-weight-700 text-dark"
                style={{ fontSize: "1.8rem", lineHeight: "1.2" }}
              >
                {count}
                <span
                  className="text-muted ml-2"
                  style={{ fontSize: "14px", fontWeight: "400" }}
                >
                  commandes
                </span>
              </h2>
            </div>

            {/* Icon Circle */}
            <div
              className={`bg-${color}-light rounded-circle d-flex align-items-center justify-content-center`}
              style={{ width: "45px", height: "45px" }}
            >
              <i
                className={`fa ${icon} text-${color}`}
                style={{ fontSize: "20px" }}
              ></i>
            </div>
          </div>

          {/* Divider line */}
          <hr
            className="my-3"
            style={{ borderTop: "1px solid #f1f1f4", margin: "15px 0" }}
          />

          {/* Bottom section: Financial Revenue */}
          <div className="d-flex justify-content-between align-items-center">
            <span className="text-muted" style={{ fontSize: "13px" }}>
              Chiffre d'Affaires
            </span>
            <span
              className={`text-${color} font-weight-700`}
              style={{ fontSize: "15px" }}
            >
              {formattedRevenue} DT
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Main;
